import type { CompanyIdNameParentIdDto } from "@vintecc/capture-api-types/v1";

export interface CompanyWithSubcompanies extends CompanyIdNameParentIdDto {
  subcompanies: CompanyWithSubcompanies[];
}

/**
 * Transforms a flat list of companies into a hierarchical tree structure.
 * Optionally filters the tree based on a search term, preserving parent-child relationships.
 */
export const transformCompanyListToTree = (companyList: CompanyIdNameParentIdDto[], searchTerm?: string): CompanyWithSubcompanies[] => {
  if (!companyList) return [];

  const companyMap = new Map<number, CompanyWithSubcompanies>();

  // First pass: create all companies with empty subcompanies arrays
  companyList.forEach((company) => {
    companyMap.set(company.id!, { ...company, subcompanies: [] });
  });

  // Second pass: populate subcompanies arrays
  companyList.forEach((company) => {
    if (company.parentId && company.parentId !== company.id) {
      const parent = companyMap.get(company.parentId);
      if (parent) {
        const currentCompany = companyMap.get(company.id!);
        if (currentCompany) {
          parent.subcompanies.push(currentCompany);
        }
      }
    }
  });

  // Get root companies
  const rootCompanies = Array.from(companyMap.values()).filter(
    (company) => !company.parentId || company.parentId === company.id || companyMap.has(company.parentId) === false,
  );

  // Apply search filter after tree is built
  if (searchTerm) {
    const filterTree = (companies: CompanyWithSubcompanies[]): CompanyWithSubcompanies[] => {
      return companies.reduce<CompanyWithSubcompanies[]>((acc, company) => {
        const matchesSearch = company.name?.toLowerCase().includes(searchTerm.toLowerCase());
        const filteredSubcompanies = filterTree(company.subcompanies);

        if (matchesSearch || filteredSubcompanies.length > 0) {
          acc.push({ ...company, subcompanies: filteredSubcompanies });
        }

        return acc;
      }, []);
    };

    return filterTree(rootCompanies);
  }

  return rootCompanies;
};
